import { motion } from 'framer-motion'
import { ArrowRight, BarChart, Globe, Lock, Zap, Users, Shield, TrendingUp } from 'lucide-react'

export default function HomePage() {
  return (
    <div className="relative">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 dark:from-blue-900 dark:via-blue-800 dark:to-indigo-900 px-6 py-20 sm:py-28 lg:px-8">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="relative mx-auto max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="mb-6">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-200 text-sm font-medium">
                ✨ 원클릭으로 생성되는 단축URL!
              </span>
            </div>
            <h1 className="text-5xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-7xl">
              <span className="block">빠르고 안전한</span>
              <span className="block bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                단축URL 서비스
              </span>
            </h1>
            <p className="mt-6 text-xl leading-8 text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              긴 인터넷 주소를 짧고 기억하기 쉬운 링크로 변환해보세요. 
              전문가급 분석 기능과 브랜드 커스터마이징이 가능합니다.
            </p>
            <div className="mt-10 flex items-center justify-center">
              <a
                href="https://app.shorty.kr/dashboard"
                className="btn btn-primary text-lg px-8 py-4 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                무료로 시작하기
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Features Section */}
      <section className="py-24 sm:py-32 bg-white dark:bg-gray-900">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-2xl text-center"
          >
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              <span className="block">완벽한 링크 관리를 위한</span>
              <span className="block bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                전문가급 기능들
              </span>
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              비즈니스 성장에 필요한 모든 기능을 하나의 플랫폼에서 제공합니다
            </p>
          </motion.div>
          
          <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((feature, index) => (
              <motion.div
                key={feature.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group card p-8 hover:shadow-xl transition-all duration-300 hover:bg-gradient-to-br hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-900/50 dark:hover:to-indigo-900/50"
              >
                <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white group-hover:from-blue-600 group-hover:to-blue-700 transition-all duration-300">
                  <feature.icon className="h-7 w-7" />
                </div>
                <h3 className="mt-6 text-xl font-semibold text-gray-900 dark:text-white">
                  {feature.name}
                </h3>
                <p className="mt-3 text-gray-600 dark:text-gray-300 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/50 dark:to-indigo-900/50">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <h2 className="text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              왜 <span className="text-blue-600">Shorty.kr</span>을 선택해야 할까요?
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300">
              다른 서비스와 차별화된 우리만의 강점을 확인해보세요
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={advantage.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="card p-8 text-center hover:shadow-lg transition-all duration-300"
              >
                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900 mb-6">
                  <advantage.icon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  {advantage.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {advantage.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-700 dark:to-blue-900">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold tracking-tight text-white sm:text-5xl">
              지금 바로 시작해보세요!
            </h2>
            <p className="mt-6 text-xl leading-8 text-blue-100 max-w-2xl mx-auto">
              몇 초 만에 계정을 만들고 첫 번째 단축 URL을 생성해보세요. 
              무료로 시작하여 필요에 따라 업그레이드할 수 있습니다.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.shorty.kr/dashboard"
                className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-blue-600 bg-white rounded-lg hover:bg-gray-50 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                무료로 시작하기
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
              <button className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white border-2 border-white rounded-lg hover:bg-white hover:text-blue-600 transition-all duration-200">
                문의하기
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  )
}

const features = [
  {
    name: '실시간 분석',
    description: '클릭 수, 지역 데이터, 사용자 행동을 실시간으로 추적하고 상세한 인사이트를 제공합니다.',
    icon: BarChart,
  },
  {
    name: '커스텀 도메인',
    description: '브랜드 전용 도메인을 사용하여 전문적인 단축 링크를 만들 수 있습니다.',
    icon: Globe,
  },
  {
    name: '엔터프라이즈 보안',
    description: '악성 링크 탐지 및 고급 보안 기능으로 안전한 링크 관리를 보장합니다.',
    icon: Lock,
  },
  {
    name: '초고속 처리',
    description: '글로벌 CDN과 엣지 캐싱으로 100ms 이하의 빠른 리다이렉션을 제공합니다.',
    icon: Zap,
  },
]

const advantages = [
  {
    title: '사용자 친화적',
    description: '직관적인 인터페이스로 누구나 쉽게 사용할 수 있습니다. 복잡한 설정 없이 바로 시작하세요.',
    icon: Users,
  },
  {
    title: '강력한 보안',
    description: 'HTTPS 보안 프로토콜과 실시간 위협 감지로 안전한 링크 관리를 보장합니다.',
    icon: Shield,
  },
  {
    title: '확장 가능',
    description: '개인 사용자부터 대기업까지, 모든 규모의 비즈니스에 맞는 솔루션을 제공합니다.',
    icon: TrendingUp,
  },
]
